import { useEffect, useState } from "react";
import { Badge, Button, Col, Nav, NavItem, NavLink, Row, Table, UncontrolledTooltip } from "reactstrap";
import Confirm from "../../common/Confirm";
import FilterBadge from "../../common/FilterBadge";
import FilterInput from "../../common/FilterInput";
import { isMultiValue, KaSelect, KaSelectOption, toKaSelectOptions } from "../../common/KaSelect";
import { Query } from "../../common/lib/fetch/useQuery";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import MultiQueryView from "../../common/lib/MultiQueryView";
import OverflowTooltip from "../../common/OverflowTooltip";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import useGetSiteSettings from "../../requests/useGetSiteSettings";
import { StagedOrderDetails, StagedOrderStep, useGetStagedOrders } from "../../requests/useGetStagedOrders";
import useLoadingCompleted from "../../requests/useLoadingCompleted";
import { useRemoveStagedOrder } from "../../requests/useRemoveStagedOrder";
import { useCompleteStagedOrderWithoutSignature } from "../../requests/useCompleteStagedOrderWithoutSignature";
import { BaySettings } from "./BayDirections/BayDirections";
// TODO: Uncomment import ReassignBayModal from "./ReassignBayModal";
import StagedOrderFilterModal from "./StagedOrderFilterModal";
import useLocalStorage from "../../common/lib/hooks/useLocalStorage";

export type FilterSettings = {
  bay: string;
  stagedOrderStep: string;
}

export enum SelectableColumns {
    DriverEmail = "Driver Email",
    DriverNumber = "Driver #",
    Transport = "Transport",
    Customer = "Customer",
    Products = "Products",
    Amount = "Amount",
    Status = "Status",
    Bay = "Bay",
    Destination = "Destination",
    Created = "Created",
}

const StagedOrders = () => {
  const displayMessage = useDisplayMessage();
  const useGetStagedOrdersQuery = useGetStagedOrders();
  const [loaderViewActive, setLoaderViewActive] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  // TODO: Uncomment const [stagedOrderToReassignBay, setStagedOrderToReassignBay] = useState<StagedOrderDetails | undefined>(undefined);

  const [selectedColumns, setSelectedColumns] = useLocalStorage<string[]>("stagedOrders-selectedColumns", Object.values(SelectableColumns).map(s => s.toString()));

  const getBaySettings = useGetSiteSettings<BaySettings>();

  const [filters, setFilters] = useLocalStorage<FilterSettings>("stagedOrders-filters", {
    bay: '',
    stagedOrderStep: '',
  });

  const updateFilters = (newFilters: Partial<FilterSettings>) =>
    setFilters({ ...filters, ...newFilters });

  const changeView = (activateLoaderView: boolean) => {
    setLoaderViewActive(activateLoaderView);
    useGetStagedOrdersQuery.query();
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const interval = setInterval(() => {
      useGetStagedOrdersQuery.query();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const stagedOrderStepOptions = toKaSelectOptions(Object.values(StagedOrderStep).map(s => ({ label: s.replace(/([A-Z])/g, ' $1').trim(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );
  const filterStep = stagedOrderStepOptions.find((option) => option.value === filters.stagedOrderStep);

  const bayOptions = toKaSelectOptions(getBaySettings.data?.bays ?? [],
    "id",
    "name",
    { sortByLabel: true }
  );
  const filterBay = bayOptions.find((option) => option.value === filters.bay);

  const stagedOrdersFiltered = useGetStagedOrdersQuery.data?.filter((so) => 
    (so.orderNumber.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      so.orderNumbers.some(o => o.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
      so.customers.some(c => c.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
      so.transport.toLowerCase().includes(debouncedSearch.toLowerCase())) &&
    (filters.bay ? so.bay === filterBay?.label : true) &&
    (filters.stagedOrderStep ? so.step === filterStep?.value : true)
  );

  const selectableColumnOptions = toKaSelectOptions(Object.values(SelectableColumns).map<KaSelectOption>(s => ({ label: s.toString(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );

  return <>
    {/* TODO: Uncomment
    {stagedOrderToReassignBay && <ReassignBayModal
      stagedOrder={stagedOrderToReassignBay}
      onSuccess={(m) => displayMessage.success(m)}
      onError={(m) => displayMessage.fail(m)}
      onClose={() => {
        setStagedOrderToReassignBay(undefined);
        useGetStagedOrdersQuery.query();
      }} />}
    */}
    <MultiQueryView
    queries={{ stagedOrders: useGetStagedOrdersQuery, bays: getBaySettings }}
    renderData={response => <>
      <div className="d-flex justify-content-between">
        <h2>Staged Orders</h2>
        <div>
          <FilterBadge
            label="Status"
            value={filterStep?.label}
            onClose={() => updateFilters({ stagedOrderStep: '' })} />
          <FilterBadge
            label="Bay"
            value={filterBay?.label}
            onClose={() => updateFilters({ bay: '' })} />
          <Button className="py-0"
            color="link"
            data-testid="refresh-button"
            onClick={() => useGetStagedOrdersQuery.query()}>
            <i className="bi-arrow-clockwise" style={{ fontSize: '2rem' }} />
          </Button>
        </div>
      </div>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <Row>
        <Col className="d-flex justify-content-between">
          <form className="col col-lg-5 my-auto mx-3">
            <Row>
              <Col className="col-auto px-0 m-auto flex-fill">
                <FilterInput placeholder='Search orders, customers, transport...' value={search} onFilterTextChange={setSearch} />
              </Col>
              <Col className="col-auto px-2">
                <Button className="p-0"
                  color="link"
                  data-testid="show-filter-modal"
                  onClick={() => setShowFilterModal(true)}>
                  <i className="bi-filter" style={{ fontSize: '2rem' }} />
                </Button>
              </Col>
            </Row> 
          </form>
          <Col className="col-auto">
            <Row>
              <Col className="col-auto m-auto">
                <KaSelect
                  placeholder="Select columns"
                  options={selectableColumnOptions}
                  isClearable={false}
                  controlShouldRenderValue={false}
                  closeMenuOnSelect={false}
                  isMulti={true}
                  hideSelectedOptions={false}
                  onChange={(valueObject) => {
                    if (isMultiValue(valueObject)) { setSelectedColumns(valueObject?.map(p => p.value)) }
                  }}
                  value={selectableColumnOptions.filter((option) => selectedColumns.includes(option.value))} />
              </Col>
              <Col className="col-auto">
                <Nav tabs>
                  <NavItem>
                    <NavLink data-testid='officeView' active={!loaderViewActive} onClick={() => changeView(false)} className="p-1 pt-2" style={{ height:"69px", paddingBottom:"8px", cursor: "pointer" }}>
                      <img alt="officeViewButton" className="nav-icon" style={{ height:"55px" }} src="../../../images/svg/office_view_icon.svg"/>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink data-testid='loaderView' active={loaderViewActive} onClick={() => changeView(true)} className="p-1 px-3" style={{ height:"69px", paddingBottom:"12px", cursor: "pointer" }}>
                      <img alt="loaderViewButton" className="nav-icon" style={{ height:"60px" }} src="../../../images/svg/loader_view_icon.svg"/>
                    </NavLink>
                  </NavItem>
                </Nav>
              </Col>
            </Row>
          </Col>
        </Col>
      </Row>
      <div className="mt-1">
      {!loaderViewActive
        ? <OfficeView
            displayMessageSuccess={(m) => displayMessage.success(m)}
            displayMessageFail={(m) => displayMessage.fail(m)}
            getStagedOrders={useGetStagedOrdersQuery}
            stagedOrders={stagedOrdersFiltered!}
            selectedColumns={selectedColumns}
            onReassignBay={(_so) => {}} /* TODO: Uncomment onReassignBay={setStagedOrderToReassignBay} */ />
        : <LoaderView
            displayMessageSuccess={(m) => displayMessage.success(m)}
            displayMessageFail={(m) => displayMessage.fail(m)}
            getStagedOrders={useGetStagedOrdersQuery}
            stagedOrders={stagedOrdersFiltered!} 
            selectedColumns={selectedColumns} />}
      </div>
      {showFilterModal && <StagedOrderFilterModal
        bayOptions={bayOptions}
        stagedOrderStepOptions={stagedOrderStepOptions}
        filters={filters}
        updateFilters={updateFilters}
        toggle={() => setShowFilterModal(false)} />}
    </>
    } />
  </>
}

interface OfficeViewProps {
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
  getStagedOrders: Query<StagedOrderDetails[]>;
  stagedOrders: StagedOrderDetails[];
  selectedColumns: string[];
  onReassignBay: (so: StagedOrderDetails) => void;
}

const OfficeView = (props: OfficeViewProps) => {
  const [stagedOrderToRemove, setStagedOrderToRemove] = useState<StagedOrderDetails | undefined>(undefined);
  const [stagedOrderToCompleteWithoutSignature, setStagedOrderToCompleteWithoutSignature] = useState<StagedOrderDetails | undefined>(undefined);

  const removeStagedOrder = useRemoveStagedOrder(stagedOrderToRemove?.id ?? "", {
    onSuccess: () => {
      props.displayMessageSuccess(`Staged order for order number ${stagedOrderToRemove?.orderNumber} successfully removed`);
      setStagedOrderToRemove(undefined);
      props.getStagedOrders.query();
    },
    onError: (err) => {
      setStagedOrderToRemove(undefined);
      props.displayMessageFail(adminFacingErrorMessage(err))
    }
  });

  const completeStagedOrderWithoutSignature = useCompleteStagedOrderWithoutSignature(stagedOrderToCompleteWithoutSignature?.id ?? "", {
    onSuccess: () => {
      props.displayMessageSuccess(`A ticket for Staged Order ${stagedOrderToCompleteWithoutSignature?.orderNumber} will be printed without a signature. Please retrieve it from the printer.`);
      setStagedOrderToCompleteWithoutSignature(undefined);
      props.getStagedOrders.query();
    },
    onError: (err) => {
      setStagedOrderToCompleteWithoutSignature(undefined);
      props.displayMessageFail(adminFacingErrorMessage(err))
    }
  });

  return <>
    {stagedOrderToRemove && <Confirm
      visible={!!stagedOrderToRemove}
      title={"Remove Staged Order"}
      body={`Are you sure you want to remove Staged Order for Order ${stagedOrderToRemove.orderNumber}?`}
      loading={removeStagedOrder.isLoading}
      onConfirm={() => removeStagedOrder.request({})}
      onDismiss={() => setStagedOrderToRemove(undefined)} />}
    {stagedOrderToCompleteWithoutSignature && <Confirm
      visible={!!stagedOrderToCompleteWithoutSignature}
      title={"Complete Staged Order without Signature"}
      body={`Are you sure you want to complete Staged Order ${stagedOrderToCompleteWithoutSignature.orderNumber} without collecting a signature?`}
      loading={completeStagedOrderWithoutSignature.isLoading}
      onConfirm={() => completeStagedOrderWithoutSignature.request({})}
      onDismiss={() => setStagedOrderToCompleteWithoutSignature(undefined)} />}
    <Table className="p-3 mb-3">
      <thead>
        <tr className="p-1 mb-3 bg-primary bg-opacity-10">
          <th>Order #</th>
          {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <th>Driver Email</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <th>Driver #</th>}
          {props.selectedColumns.includes(SelectableColumns.Transport) && <th>Transport</th>}
          {props.selectedColumns.includes(SelectableColumns.Customer) && <th>Customer</th>}
          {props.selectedColumns.includes(SelectableColumns.Products) && <th>Products</th>}
          {props.selectedColumns.includes(SelectableColumns.Amount) && <th>Amount</th>}
          {props.selectedColumns.includes(SelectableColumns.Status) && <th>Status</th>}
          {props.selectedColumns.includes(SelectableColumns.Bay) && <th>Bay</th>}
          {props.selectedColumns.includes(SelectableColumns.Destination) && <th>Destination</th>}
          {props.selectedColumns.includes(SelectableColumns.Created) && <th>Created</th>}
          <th className="col-1">Actions</th>
        </tr>
      </thead>
      <tbody>
        {props.stagedOrders.sort(alphabeticallyByName).map((so) =>
          <tr key={so.id} className="border-bottom border-1">
            <td>
              <div className="d-flex justify-content-between">
                <OverflowTooltip text={so.orderNumber + " "} />
                {so.orderNumbers.length > 0 ? <><i
                  className="bi-info-circle ps-1"
                  id={`info-${so.id}`} />
                    <UncontrolledTooltip target={`info-${so.id}`} >
                      {`Orders: ${so.orderNumbers.reduce((o1, o2) => o1 + ', ' + o2)}`}
                    </UncontrolledTooltip>
                </> : <></>}
              </div>
            </td>
            {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <td><OverflowTooltip text={so.userEmail} /></td>}
            {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <td>{so.driverNumber}</td>}
            {props.selectedColumns.includes(SelectableColumns.Transport) && <td>{so.transport}</td>}
            {props.selectedColumns.includes(SelectableColumns.Customer) && <td><OverflowTooltip text={so.customers.reduce((c1, c2) => c1 + ', ' + c2)} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Products) && <td><OverflowTooltip text={so.products.reduce((p1, p2) => p1 + ', ' + p2)} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Amount) && <td>{so.availableToRequest + " lb"}</td>}
            {props.selectedColumns.includes(SelectableColumns.Status) && <td>{formatStagedOrderStep(so.step)}</td>}
            {props.selectedColumns.includes(SelectableColumns.Bay) && <td>{so.bay}</td>}
            {props.selectedColumns.includes(SelectableColumns.Destination) && <td><OverflowTooltip text={so.destinations.length > 0 ? so.destinations.reduce((d1, d2) => d1 + ', ' + d2) : ''} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Created) && <td>{new Date(so.createdAt).toLocaleDateString()}</td>}
            <td>
              <div className="dropdown">
                <Button
                  className="dropdown-toggle btn-ghost-secondary"
                  color="link"
                  id="actionsMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ marginTop: '3px' }}>
                  <strong>...</strong>
                </Button>
                <div className="dropdown-menu" aria-labelledby="actionsMenuButton">
                  {/* TODO: Uncomment
                  {!so.locked && so.step === StagedOrderStep.BayDirections && <Button
                    className="btn btn-ghost-secondary"
                    color="link"
                    onClick={() => props.onReassignBay(so)}>
                      Reassign Bay
                  </Button>}
                  */}
                  {so.step !== StagedOrderStep.Signature && so.step !== StagedOrderStep.TicketInstructions && <Button
                    className="btn btn-ghost-danger"
                    color="link"
                    onClick={() => setStagedOrderToRemove(so)}>
                      Remove
                  </Button>}
                  {so.step === StagedOrderStep.Signature && <Button
                    className="btn btn-ghost-danger"
                    color="link"
                    onClick={() => setStagedOrderToCompleteWithoutSignature(so)}>
                      Complete Without Signature
                  </Button>}
                  {so.locked && so.step === StagedOrderStep.TicketInstructions && <p className="px-2 mb-0">No actions available</p>}
                </div>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  </>
}

interface LoaderViewProps {
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
  getStagedOrders: Query<StagedOrderDetails[]>;
  stagedOrders: StagedOrderDetails[];
  selectedColumns: string[];
}

const LoaderView = (props: LoaderViewProps) => {
  const [stagedOrderToLoad, setStagedOrderToLoad] = useState<StagedOrderDetails | undefined>(undefined);

  const loadingCompleted = useLoadingCompleted(stagedOrderToLoad?.id ?? "", {
    onSuccess: () => {
      props.displayMessageSuccess(`Staged order for order number ${stagedOrderToLoad?.orderNumber} successfully loaded`);
      setStagedOrderToLoad(undefined);
      props.getStagedOrders.query();
    },
    onError: (err) => {
      setStagedOrderToLoad(undefined);
      props.displayMessageFail(adminFacingErrorMessage(err))
    }
  });

  const filteredSortedStagedOrders = props.stagedOrders
    .filter((so) => so.step !== StagedOrderStep.Gross && so.step !== StagedOrderStep.Signature && so.step !== StagedOrderStep.TicketInstructions && !so.manuallyLoaded)
    .sort(byPriorityThenByName);

  return <>
    {stagedOrderToLoad && <Confirm
      visible={!!stagedOrderToLoad}
      title={"Load Staged Order"}
      body={`Are you sure you want to mark Staged Order for Order ${stagedOrderToLoad.orderNumber} as loaded?`}
      loading={loadingCompleted.isLoading}
      onConfirm={() => loadingCompleted.request()}
      onDismiss={() => setStagedOrderToLoad(undefined)} />}
    <Table className="fs-6">
      <thead>
        <tr className="p-1 mb-3 bg-primary bg-opacity-10">
          <th>Order #</th>
          {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <th>Driver Email</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <th>Driver #</th>}
          {props.selectedColumns.includes(SelectableColumns.Transport) && <th>Transport</th>}
          {props.selectedColumns.includes(SelectableColumns.Customer) && <th>Customer</th>}
          {props.selectedColumns.includes(SelectableColumns.Products) && <th>Products</th>}
          {props.selectedColumns.includes(SelectableColumns.Amount) && <th>Amount</th>}
          {props.selectedColumns.includes(SelectableColumns.Status) && <th>Status</th>}
          {props.selectedColumns.includes(SelectableColumns.Bay) && <th>Bay</th>}
          {props.selectedColumns.includes(SelectableColumns.Destination) && <th>Destination</th>}
          {props.selectedColumns.includes(SelectableColumns.Created) && <th>Created</th>}
          <th className="col-1">Actions</th>
        </tr>
      </thead>
      <tbody>
        {filteredSortedStagedOrders.map(so =>
          <tr key={so.id} className="border-bottom border-1">
            <td>
              <div className="d-flex justify-content-between">
                <OverflowTooltip text={so.orderNumber + " "} />
                {so.orderNumbers.length > 0 ? <><i
                  className="bi-info-circle ps-1"
                  id={`info-${so.id}`} />
                    <UncontrolledTooltip target={`info-${so.id}`} >
                      {`Orders: ${so.orderNumbers.reduce((o1, o2) => o1 + ', ' + o2)}`}
                    </UncontrolledTooltip>
                </> : <></>}
              </div>
            </td>
            {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <td><OverflowTooltip text={so.userEmail} /></td>}
            {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <td>{so.driverNumber}</td>}
            {props.selectedColumns.includes(SelectableColumns.Transport) && <td>{so.transport}</td>}
            {props.selectedColumns.includes(SelectableColumns.Customer) && <td><OverflowTooltip text={so.customers.reduce((c1, c2) => c1 + ', ' + c2)} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Products) && <td><OverflowTooltip text={so.products.reduce((p1, p2) => p1 + ', ' + p2)} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Amount) && <td>{so.availableToRequest + " lb"}</td>}
            {props.selectedColumns.includes(SelectableColumns.Status) && <td>{formatStagedOrderStep(so.step)}</td>}
            {props.selectedColumns.includes(SelectableColumns.Bay) && <td>{so.bay}</td>}
            {props.selectedColumns.includes(SelectableColumns.Destination) && <td><OverflowTooltip text={so.destinations.length > 0 ? so.destinations.reduce((d1, d2) => d1 + ', ' + d2) : ''} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Created) && <td>{new Date(so.createdAt).toLocaleDateString()}</td>}
            <td>
              {so.step === StagedOrderStep.BayDirections && <Button
                className="btn-danger btn-sm"
                onClick={() => setStagedOrderToLoad(so)}>
                <strong>Load</strong>
              </Button>}
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  </>
}

const formatStagedOrderStep = (step: StagedOrderStep) => <Badge className={`bg-stagedOrderStep-${step}`} style={{textWrap: "wrap"}}>{step.replace(/([A-Z])/g, ' $1').trim()}</Badge>;

const alphabeticallyByName = (a: StagedOrderDetails, b: StagedOrderDetails) => a.orderNumber.localeCompare(b.orderNumber);

const byPriorityThenByName = (a: StagedOrderDetails, b: StagedOrderDetails) => Object.values(StagedOrderStep).indexOf(b.step) - Object.values(StagedOrderStep).indexOf(a.step) || a.orderNumber.localeCompare(b.orderNumber);

export { StagedOrders };